import Button from '@/components/UI/Button';
import SpreadSheet from '@/components/UI/SpreadSheet';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import HotTable from '@handsontable//react';
import { _isEmpty, _notNil, _isNotEmpty } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { PlannedAnimalDefinition } from './PlannedAnimals/PlannedAnimals.utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TreatmentGroup } from '@/model/TreatmentGroup.model.ts';
import { AnimalMapV1 } from '@/model/PlannedAnimal.model.ts';
import Switch from '@/components/UI/FormElements/Switch';
import {
  type MapAnimalRow,
  transformAnimalsToMapData,
  mapAnimalsSettings,
  updateCellMeta,
  processDataChange,
  createAnimalMappings,
  mapAnimalsColumnData,
} from './MapAnimals.utils';
import './PlannedAnimals.scss';
import { errorToast } from '@/helpers.tsx';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import InVivoError from '@/model/InVivoError.ts';

interface MapAnimalsProps {
  animals: Array<Animal>;
  plannedAnimals: Array<PlannedAnimalDefinition>;
  groups: Array<TreatmentGroup>;
  onSubmit: (mappedAnimals: AnimalMapV1) => Promise<void>;
}

const MapAnimals: React.FC<MapAnimalsProps> = ({ animals, plannedAnimals, groups, onSubmit }) => {
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [mapData, setMapData] = useState<Array<MapAnimalRow>>([]);
  const [overrideNames, setOverrideNames] = useState<boolean>(false);

  const ref = useRef<HotTable>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (_isNotEmpty(animals)) {
      const transformedData = transformAnimalsToMapData(animals, plannedAnimals);
      setMapData(transformedData);
    }
  }, [animals, plannedAnimals]);

  useEffect(() => {
    const hot = ref.current?.hotInstance;
    if (hot && mapData.length > 0) {
      updateCellMeta(hot, mapData, groups, plannedAnimals);
    }
  }, [mapData, groups, plannedAnimals]);

  const columnData = useMemo(() => {
    return mapAnimalsColumnData(groups, ref);
  }, [groups]);

  const settings = useMemo(() => {
    return mapAnimalsSettings(columnData);
  }, [columnData]);

  const closeModal = () => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  };

  const handleDataChange = (changes: any, source: string) => {
    if (!changes || source === 'loadData') {
      return;
    }
    const hotInstance = ref.current?.hotInstance ?? null;
    if (hotInstance) {
      const updatedMapData = processDataChange(hotInstance, plannedAnimals);
      setMapData(updatedMapData);
    }
  };

  const submitMapping = async () => {
    if (!isInvalid && !isSubmitting && _notNil(onSubmit)) {
      try {
        setIsSubmitting(true);
        const mappedAnimals = createAnimalMappings(mapData);
        if (_isNotEmpty(mappedAnimals)) {
          await onSubmit({
            mappings: mappedAnimals,
            override_names: overrideNames,
          });
        }
        closeModal();
      } catch (err) {
        errorToast('Could not submit mapping request');
        ExceptionHandler.captureException(
          new InVivoError('Could not map planned animals', {
            cause: err,
            slug: 'map-animals-modal-error',
          })
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (_isEmpty(animals)) {
    return (
      <div
        data-test-component="EmptyAnimals"
        data-test-element="container"
        className="flex flex-wrap overflow-hidden w-50 center ma3 bg-light-gray br2 mt4 mb-2 relative"
      >
        <div className="pa4">
          <div className="text-center">
            <h3 className="mb2">No animals found</h3>
            <p className="mb3">Registered animals must be assigned to groups before mapping planned animals.</p>
            <Button plain onClick={closeModal}>
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (_isEmpty(plannedAnimals)) {
    return (
      <div className="mapContainer" data-test-component="EmptyPlannedAnimals" data-test-element="container">
        <div className="pa4">
          <div className="text-center">
            <h3 className="mb2">No planned animals found</h3>
            <p className="mb3">There are no planned animals available to map from.</p>
            <Button plain onClick={closeModal}>
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mapContainer" data-test-component="MapAnimals" data-test-element="container">
      <div className="mapContentWrapper">
        <div className="mapHeaderSection">
          <h3 className="normal f4 lh-title">Map animals to planned animals</h3>
          <p className="lh-copy f5 pt2">
            Map existing animals to planned animals by selecting an available planned animal ID. Animals are
            pre-filtered to match group and sex with planned animals.
          </p>
        </div>
        <div className="ph4 pb4 flex justify-between items-center">
          <div className="flex items-center">
            <Switch
              testId="overrideNames"
              value={overrideNames}
              onChange={() =>
                setOverrideNames((prev) => {
                  return !prev;
                })
              }
            />
            <label className="ml2 mb0">Overwrite animal names with planned IDs</label>
          </div>
        </div>
        <div
          className="ow-spreadsheet-styles mapSpreadsheetContainer"
          data-test-component="MapAnimals"
          data-test-element="spreadsheet-container"
        >
          <SpreadSheet
            data={mapData}
            settings={settings}
            innerRef={ref}
            className="mapAnimals"
            buttonClasses={'absolute-sheet-buttons'}
            setInvalid={setIsInvalid}
            afterChange={handleDataChange}
            addCol={false}
          />
        </div>
      </div>
      <div
        className="ph4 pv3 flex self-end bt b--moon-gray w-100"
        data-test-component="MapAnimals"
        data-test-element="action-container"
      >
        <Button
          onClick={submitMapping}
          testId="save-action"
          disabled={isInvalid || isSubmitting}
          tooltip={isInvalid ? spreadSheetValidationTooltip : undefined}
        >
          Map Animals
        </Button>
        <Button plain className="ml3" onClick={closeModal} testId="cancel-action">
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default MapAnimals;
